import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { createWorld } from '@/services/worlds'
import type { WorldStatus } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function WorldCreate() {
  const navigate = useNavigate()
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()

  const [name, setName] = useState('')
  const [tickIntervalHours, setTickIntervalHours] = useState('24')
  const [status, setStatus] = useState<WorldStatus>('setup')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    const tickInterval = Number(tickIntervalHours)
    if (!tickInterval || tickInterval <= 0) {
      setError('Tick interval must be a positive number')
      return
    }

    setIsSubmitting(true)

    try {
      const world = await createWorld({
        name: name.trim(),
        tick_interval_hours: tickInterval,
        status,
      })
      navigate(`/worlds/${world.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create world')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/worlds')
  }

  // Show loading while checking admin status
  if (isAdminLoading) {
    return (
      <AppLayout breadcrumbs={[{ label: 'Worlds', href: '/worlds' }, { label: 'Create World' }]}>
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </AppLayout>
    )
  }

  // Redirect non-admins
  if (!isAdmin) {
    return (
      <AppLayout breadcrumbs={[{ label: 'Worlds', href: '/worlds' }, { label: 'Create World' }]}>
        <div className="px-6 py-8">
          <div className="mx-auto max-w-7xl">
            <Card className="mx-auto max-w-md">
              <CardHeader>
                <CardTitle>Access Denied</CardTitle>
                <CardDescription>Only administrators can create new worlds.</CardDescription>
              </CardHeader>
              <CardFooter>
                <Button variant="outline" onClick={() => navigate('/worlds')}>
                  Back to Worlds
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={[{ label: 'Worlds', href: '/worlds' }, { label: 'Create World' }]}>
      <div className="px-6 py-8">
        <div className="mx-auto max-w-7xl">
          <Card className="mx-auto max-w-lg">
            <CardHeader>
              <CardTitle>Create New World</CardTitle>
              <CardDescription>Set up a new game world for players to join.</CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter world name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tickIntervalHours">Tick Interval (hours)</Label>
                  <Input
                    id="tickIntervalHours"
                    type="number"
                    min="1"
                    placeholder="24"
                    value={tickIntervalHours}
                    onChange={(e) => setTickIntervalHours(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                  <p className="text-muted-foreground text-xs">
                    How often the game advances (1 tick = 3 in-game months)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value: string) => setStatus(value as WorldStatus)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="setup">Setup</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    Setup: Configure world before starting. Active: Game in progress.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create World'}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
